import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';

export const ChatContext = createContext();

const URL_CHAT_AI = process.env.REACT_APP_URL_CHAT_AI;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` } });

export const ChatContextProvider = ({ children }) => {
    const { user } = useContext(AuthContext);

    // Sólo UI (no es dato de negocio) — se mantiene en localStorage por conveniencia.
    const [isOpen, setIsOpen] = useState(() => {
        try {
            return localStorage.getItem('chat_isOpen') === 'true';
        } catch (e) {
            return false;
        }
    });

    const [conversaciones, setConversaciones] = useState([]);
    const [activeConv, setActiveConv] = useState(null); // { _id, titulo }
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingConv, setLoadingConv] = useState(false);

    useEffect(() => {
        localStorage.setItem('chat_isOpen', isOpen);
    }, [isOpen]);

    // Al cambiar de usuario (login/logout), limpiamos el estado en memoria — las conversaciones
    // ahora viven en el servidor, escopadas por el JWT.
    useEffect(() => {
        setActiveConv(null);
        setMessages([]);
        setConversaciones([]);
    }, [user?.id]);

    const cargarConversaciones = async () => {
        try {
            const { data } = await axios.get(`${URL_CHAT_AI}/conversaciones`, authHeaders());
            setConversaciones(data.conversaciones || []);
        } catch (e) {
            console.error('Error cargando conversaciones:', e);
        }
    };

    const seleccionarConversacion = async (id) => {
        setLoadingConv(true);
        try {
            const { data } = await axios.get(`${URL_CHAT_AI}/conversaciones/${id}`, authHeaders());
            setActiveConv({ _id: data.conversacion._id, titulo: data.conversacion.titulo });
            setMessages(data.conversacion.mensajes || []);
        } catch (e) {
            console.error('Error cargando conversación:', e);
        } finally {
            setLoadingConv(false);
        }
    };

    const nuevaConversacion = () => {
        setActiveConv(null);
        setMessages([]);
    };

    const eliminarConversacion = async (id) => {
        try {
            await axios.delete(`${URL_CHAT_AI}/conversaciones/${id}`, authHeaders());
            setConversaciones(prev => prev.filter(c => c._id !== id));
            if (activeConv?._id === id) nuevaConversacion();
        } catch (e) {
            console.error('Error eliminando conversación:', e);
        }
    };

    const enviar = async (texto) => {
        const userMsg = { role: 'user', content: texto };
        setMessages(prev => [...prev, userMsg]);
        setIsLoading(true);

        try {
            const { data } = await axios.post(`${URL_CHAT_AI}/ask`, {
                message: texto,
                id_conversacion: activeConv?._id || null
            }, authHeaders());

            const botResponse = data.response;
            const botMsg = {
                role: 'assistant',
                content: botResponse.text || (typeof botResponse === 'string' ? botResponse : JSON.stringify(botResponse)),
                chart: botResponse.chart || null,
                rawData: botResponse.rawData || null
            };
            setMessages(prev => [...prev, botMsg]);

            if (!activeConv) {
                setActiveConv({ _id: data.id_conversacion, titulo: data.titulo });
                setConversaciones(prev => [{ _id: data.id_conversacion, titulo: data.titulo, updatedAt: new Date().toISOString() }, ...prev]);
            } else {
                setConversaciones(prev => prev.map(c => c._id === activeConv._id ? { ...c, updatedAt: new Date().toISOString() } : c));
            }
        } catch (error) {
            console.error("Error en Chat AI:", error);
            const errorMsg = error.response?.status === 403
                ? "No tienes permisos para usar esta función."
                : "Lo siento, tuve un problema conectando con el servidor.";
            setMessages(prev => [...prev, { role: 'assistant', content: errorMsg, error: true }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ChatContext.Provider value={{
            isOpen,
            setIsOpen,
            conversaciones,
            activeConv,
            messages,
            isLoading,
            loadingConv,
            cargarConversaciones,
            seleccionarConversacion,
            nuevaConversacion,
            eliminarConversacion,
            enviar
        }}>
            {children}
        </ChatContext.Provider>
    );
};
